/**
 * MTNWorkspace.js — Data Workspace (SSOT Engine)
 * Bloomberg dark terminal. 5 tabs: Scope 1 / Scope 2 / Scope 3 / Circularity / Projects
 */
import React, { useState, useEffect } from "react";
import { useMTN } from "@/context/MTNContext";
import client from "@/api/client";
import { toast } from "sonner";
import { Save, Loader, CheckCircle2, ChevronDown, Database } from "lucide-react";

const TABS = [
  { id:"s1",label:"Scope 1 — Direct" },
  { id:"s2",label:"Scope 2 — Grid" },
  { id:"s3",label:"Scope 3 — Value Chain" },
  { id:"circ",label:"Circularity" },
  { id:"projects",label:"Climate Projects" },
];

const fmtN = v => { if(!v&&v!==0)return"—"; const n=Number(v); if(n>=1e6)return`${(n/1e6).toFixed(1)}M`; if(n>=1e3)return`${(n/1e3).toFixed(0)}K`; return n.toLocaleString(); };

export default function MTNWorkspace() {
  const { businessUnits, projects, reload, selectedPeriod, loading } = useMTN();
  const [activeTab, setActiveTab] = useState("s1");
  const [calculating, setCalculating] = useState(null);
  const [propagating, setPropagating] = useState(false);
  const [log, setLog] = useState([]);
  const [editVals, setEditVals] = useState({});

  const handleCalc = async (buId, payload) => {
    setCalculating(buId);
    try {
      await client.post(`/mtn/business-unit/${buId}/${selectedPeriod}/calculate`, payload);
      setPropagating(true);
      setLog(["Calculating emissions…","Updating group totals…","Propagating to Intelligence…","Updating Finance & Carbon…","Refreshing ESG readiness…","SSOT propagation complete ✓"]);
      setTimeout(async () => { await reload(); setPropagating(false); toast.success("Calculated & propagated"); }, 1500);
    } catch { toast.error("Calculation failed"); }
    finally { setCalculating(null); }
  };

  if (loading) return <div style={{ padding:40, color:"#64748b", fontSize:12 }}>Loading…</div>;

  const InputCell = ({ buId, field, value, label, unit="" }) => {
    const key = `${buId}_${field}`;
    const val = editVals[key] !== undefined ? editVals[key] : (value || 0);
    return (
      <div>
        <label style={{ fontSize:8, color:"#475569", display:"block", marginBottom:2 }}>{label}</label>
        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
          <input type="number" value={val}
            onChange={e => setEditVals(prev=>({...prev,[key]:Number(e.target.value)}))}
            style={{ background:"#0a0f1e", border:"1px solid #1e293b", borderRadius:4, padding:"3px 6px",
              color:"#f1f5f9", fontSize:10, width:"100%", outline:"none" }}
            data-testid={`input-${buId}-${field}`} />
          {unit && <span style={{ fontSize:8, color:"#475569", whiteSpace:"nowrap" }}>{unit}</span>}
        </div>
      </div>
    );
  };

  const S1_FIELDS = [
    ["diesel_litres","Diesel (L)","L/yr"],["natural_gas_m3","Natural Gas","m³/yr"],
    ["fleet_diesel_litres","Fleet Diesel","L/yr"],["fleet_petrol_litres","Fleet Petrol","L/yr"],
    ["refrigerant_kg","Refrigerants","kg/yr"],
  ];
  const S2_FIELDS = [["electricity_kwh","Grid Electricity","kWh/yr"],["solar_kwh","Solar Generation","kWh/yr"]];
  const S3_FIELDS = [["scope3_vendor_usd","Vendor Spend","USD/yr"],["logistics_km","Logistics","km/yr"],["air_travel_km","Air Travel","km/yr"]];
  const CIRC_FIELDS = [["ewaste_recovered_t","E-waste Recovered","t/yr"],["sim_recycled_count","SIMs Recycled","units/yr"],["battery_replacements","Battery Replacements","units/yr"],["equipment_reused_pct","Equipment Reuse","%"]];

  const BUTable = ({ fields }) => (
    <div style={{ overflowX:"auto" }}>
      <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
        <thead>
          <tr style={{ borderBottom:"1px solid #1e293b" }}>
            <th style={{ textAlign:"left", padding:"8px 12px", fontSize:9, color:"#475569", fontWeight:600, width:200 }}>Business Unit</th>
            {fields.map(([f,l]) => <th key={f} style={{ textAlign:"left", padding:"8px 12px", fontSize:9, color:"#475569", fontWeight:600 }}>{l}</th>)}
            <th style={{ padding:"8px 12px", fontSize:9, color:"#475569" }}>Total (tCO₂e)</th>
            <th style={{ padding:"8px 12px" }}></th>
          </tr>
        </thead>
        <tbody>
          {businessUnits.map(bu => {
            const buPending = Object.keys(editVals).some(k => k.startsWith(bu.bu_id+"_"));
            const payload = {};
            fields.forEach(([f]) => { const k=`${bu.bu_id}_${f}`; if(editVals[k]!==undefined) payload[f]=editVals[k]; });
            return (
              <tr key={bu.bu_id} style={{ borderBottom:"1px solid #0f172a" }}>
                <td style={{ padding:"8px 12px" }}>
                  <p style={{ fontSize:10, color:"#f1f5f9", fontWeight:600 }}>{bu.name?.substring(0,30)}</p>
                  <p style={{ fontSize:8, color:"#475569" }}>{bu.cluster} · {bu.evidence_level}</p>
                </td>
                {fields.map(([f,l,u]) => (
                  <td key={f} style={{ padding:"6px 12px" }}>
                    <InputCell buId={bu.bu_id} field={f} value={bu[f]} label="" unit={u} />
                  </td>
                ))}
                <td style={{ padding:"8px 12px", textAlign:"right" }}>
                  <span style={{ fontSize:10, fontWeight:700, color:"#FFCB00" }}>{fmtN(bu.total_tco2e)}</span>
                </td>
                <td style={{ padding:"8px 12px" }}>
                  {buPending && (
                    <button onClick={() => handleCalc(bu.bu_id, payload)}
                      disabled={calculating===bu.bu_id}
                      style={{ background:"#FFCB00", color:"#000", border:"none", borderRadius:5, padding:"4px 10px",
                        fontSize:9, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}
                      data-testid={`calculate-${bu.bu_id}`}>
                      {calculating===bu.bu_id ? <Loader size={9} className="animate-spin" /> : <Save size={9} />}
                      Calculate
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ padding:"24px 28px" }}>
      <div style={{ marginBottom:20 }}>
        <span style={{ fontSize:9, fontWeight:800, color:"#FFCB00", letterSpacing:".08em", textTransform:"uppercase",
          background:"#FFCB0015", border:"1px solid #FFCB0030", padding:"2px 8px", borderRadius:4 }}>MTN Nigeria</span>
        <h1 style={{ fontSize:20, fontWeight:900, color:"#f1f5f9", marginTop:6 }}>Data Workspace — SSOT Engine</h1>
        <p style={{ fontSize:11, color:"#475569" }}>Edit activity data · Save & Calculate · Propagation triggers instantly across all pages</p>
      </div>

      {/* Propagation log */}
      {propagating && (
        <div style={{ background:"#0d1423", border:"1px solid #FFCB0030", borderRadius:8, padding:12, marginBottom:16 }}>
          <p style={{ fontSize:9, fontWeight:700, color:"#FFCB00", marginBottom:8 }}>SSOT Propagation in progress…</p>
          {log.map((l,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
              <CheckCircle2 size={9} color="#22c55e" />
              <span style={{ fontSize:9, color:"#64748b" }}>{l}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, background:"#0a0f1e", border:"1px solid #1e293b", borderRadius:8, padding:6, marginBottom:16 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ flex:1, padding:"7px 8px", borderRadius:6, border:"none", cursor:"pointer", fontSize:10, fontWeight:600,
              background:activeTab===t.id?"#FFCB00":"transparent",
              color:activeTab===t.id?"#000":"#475569" }}
            data-testid={`workspace-tab-${t.id}`}>{t.label}
          </button>
        ))}
      </div>

      <div style={{ background:"#0d1423", border:"1px solid #1e293b", borderRadius:8, overflow:"hidden" }}>
        {activeTab==="s1" && <BUTable fields={S1_FIELDS} />}
        {activeTab==="s2" && <BUTable fields={S2_FIELDS} />}
        {activeTab==="s3" && <BUTable fields={S3_FIELDS} />}
        {activeTab==="circ" && <BUTable fields={CIRC_FIELDS} />}
        {activeTab==="projects" && (
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:"1px solid #1e293b" }}>
                {["Project","CAPEX","Reduction/yr","Status","Pathway","ICMA"].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"8px 12px", fontSize:9, color:"#475569", fontWeight:600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(projects.list||[]).map(p => (
                <tr key={p.project_id} style={{ borderBottom:"1px solid #0f172a" }} data-testid={`project-row-${p.project_id}`}>
                  <td style={{ padding:"8px 12px" }}>
                    <p style={{ fontSize:10, color:"#f1f5f9" }}>{p.name}</p>
                    <p style={{ fontSize:8, color:"#475569" }}>{p.type} · {p.cluster}</p>
                  </td>
                  <td style={{ padding:"8px 12px", fontSize:10, fontWeight:700, color:"#60a5fa" }}>{p.capex_usd>=1e6?`$${(p.capex_usd/1e6).toFixed(1)}M`:fmtN(p.capex_usd)}</td>
                  <td style={{ padding:"8px 12px", fontSize:10, color:"#22c55e", fontWeight:700 }}>{fmtN(p.reduction_tco2e_yr)} tCO₂e</td>
                  <td style={{ padding:"8px 12px" }}>
                    <span style={{ fontSize:8, fontWeight:700, padding:"2px 6px", borderRadius:3,
                      background:p.status==="implementation"?"#14532d30":p.status==="design"?"#FFCB0020":"#1e293b",
                      color:p.status==="implementation"?"#22c55e":p.status==="design"?"#FFCB00":"#64748b" }}>{p.status}</span>
                  </td>
                  <td style={{ padding:"8px 12px" }}>
                    <span style={{ fontSize:8, padding:"2px 6px", borderRadius:3,
                      background:p.carbon_pathway==="art6"?"#4c1d9530":"#14532d30",
                      color:p.carbon_pathway==="art6"?"#a78bfa":"#22c55e" }}>{p.carbon_pathway}</span>
                  </td>
                  <td style={{ padding:"8px 12px", fontSize:9, color:p.icma_eligible?"#22c55e":"#475569" }}>{p.icma_eligible?"✓ Eligible":"—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
